"use client";

import { FunctionComponent, forwardRef, useState, useEffect, ReactNode, Fragment } from "react";
import { Arrow, Body, Counter, Adornments, Loader, Label, Wrapper } from "./button.styles";
import { Spinner } from "../spinner/spinner";
// eslint-disable-next-line import/no-cycle
import { Popover } from "../popover/popover";
import { KeyValueObject, ButtonLink, TailwindColors } from "@components/common/types/general";
import { PlacementType } from "react-laag/dist/PlacementType";
import { Tooltip } from "../tooltip/tooltip";
import { Link } from "../link/link";

export interface ButtonProps {
    /**
     * Transform the button to a link.
     */
    link?: ButtonLink;

    /**
     * You can pass an ReactNode which will be opened in a popover when clicking the button.
     */
    popover?: ReactNode;

    /**
     * A callback function which will be fired on click.
     */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * A callback function which will be fired on mouse enter.
     */
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * A callback function which will be fired on mouse over.
     */
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * A callback function which will be fired on open/close the popover. This callback function only gets fired when `popover` isset.
     */
    openHandler?: (open: boolean) => void;

    /**
     * Caption of the button.
     */
    label?: ReactNode | string;

    /**
     * Color of the button.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * This will render an invisible caption additionally to `label`.
     */
    labelGhost?: boolean | false;

    /**
     * Type of the button.
     */
    type?: "button" | "submit" | "reset";

    /**
     * Render an id attribute in the dom.
     */
    id?: string;

    /**
     * A string which will refer to a form.
     */
    form?: string;

    /**
     * Whether it should be an outline button or not.
     */
    outline?: boolean | false;

    /**
     * Keep default text color.
     */
    keepColor?: boolean | false;

    /**
     * By default, an outline button will get a background color on hover. With this prop you can define whether it should keep the outline or not.
     */
    outlineKeepOnHover?: boolean | false;

    /**
     * Display a bottom border so that the button looks like underlined.
     */
    underline?: boolean | false;

    /**
     * Border radius of the button.
     */
    rounded?: "md" | "lg" | "full" | "l-md" | "r-md" | "l-lg" | "r-lg" | "l-full" | "r-full";

    /**
     * Left adornments of the button.
     */
    leftAdornments?: ReactNode | string;

    /**
     * Right adornments of the button.
     */
    rightAdornments?: ReactNode | string;

    /**
     * Size of the button.
     */
    size?: "xs" | "sm" | "lg";

    /**
     * A block button will get 100% width relative to the parent container.
     */
    block?: boolean | false;

    /**
     * A text button will display the button as a link but keep Y-padding, line-height and other button styles.
     */
    text?: boolean | false;

    /**
     * Y-padding and line-height can be disabled only for text buttons.
     */
    disableHeight?: boolean;

    /**
     * Force the active style of a button (similar to hover).
     */
    active?: boolean | false;

    /**
     * Will display a loading spinner and disable the button.
     */
    loading?: boolean | false;

    /**
     * Will disable the button.
     */
    disabled?: boolean | false;

    /**
     * Will display the button with shadow.
     */
    shadow?: boolean | false;

    /**
     * Set a fixed width to the button.
     */
    length?: "200" | "250" | "300" | number;

    /**
     * Define the focus state of the button.
     */
    focusState?: "default" | "hover";

    /**
     * Will remove some default button styles like border, hover, focus so it can be displayed as an icon/adornments with button functionality.
     */
    fillIcon?: boolean | false;

    /**
     * Will enlarge the button icon/adornments.
     */
    largeIcon?: boolean | false;

    /**
     * Will make border and background transparent.
     */
    clean?: boolean | false;

    /**
     * Will reflect the button by x axis.
     */
    reflectX?: boolean | false;

    /**
     * Apply tabindex attribute in the dom.
     */
    tabIndex?: number;

    /**
     * Will open a tooltip on hover
     */
    tooltip?: { text: string; placement: PlacementType };

    /**
     * Add an arrow to the button.
     */
    arrow?: boolean | false;

    /**
     * Add a counter to the button.
     */
    counter?: string;

    /**
     * Render dom attributes, eg. aria-label.
     */
    attributes?: KeyValueObject;

    /**
     * Class name of the button.
     */
    className?: string;
}

interface ButtonBodyProps {
    label?: ButtonProps["label"];
    labelGhost?: ButtonProps["labelGhost"];
    loading?: ButtonProps["loading"];
    tooltip?: ButtonProps["tooltip"];
    arrow?: ButtonProps["arrow"];
    counter?: ButtonProps["counter"];
    color?: ButtonProps["color"];
    size?: ButtonProps["size"];
    text?: ButtonProps["text"];
    disableHeight?: ButtonProps["disableHeight"];
    leftAdornments?: ButtonProps["leftAdornments"];
    rightAdornments?: ButtonProps["rightAdornments"];
}

const ButtonBody: FunctionComponent<ButtonBodyProps> = ({
    tooltip,
    label,
    labelGhost,
    arrow,
    loading,
    counter,
    size,
    text,
    disableHeight,
    leftAdornments,
    rightAdornments,
}) => {
    const CustomTag = tooltip ? Tooltip : Fragment;
    const customProps = tooltip ? { text: tooltip.text, placement: tooltip.placement } : {};

    return (
        <CustomTag {...customProps}>
            <Body size={size} text={text} disableHeight={disableHeight}>
                {leftAdornments && <Adornments size={size}>{leftAdornments}</Adornments>}
                {label && <Label>{label}</Label>}
                {labelGhost && <Label ghost={labelGhost}>-</Label>}
                {rightAdornments && <Adornments size={size}>{rightAdornments}</Adornments>}
                {arrow && (
                    <Arrow>
                        <span></span>
                    </Arrow>
                )}
                {loading && (
                    <Loader>
                        <Spinner />
                    </Loader>
                )}
                {counter && <Counter>{counter}</Counter>}
            </Body>
        </CustomTag>
    );
};

export const Button = forwardRef<HTMLDivElement, ButtonProps>(
    (
        {
            link,
            popover,
            onClick,
            onMouseEnter,
            onMouseLeave,
            type = "button",
            form,
            outline,
            openHandler,
            id,
            outlineKeepOnHover,
            underline,
            loading,
            shadow,
            active,
            text,
            disableHeight = false,
            disabled,
            tabIndex,
            block,
            fillIcon,
            largeIcon,
            clean,
            reflectX,
            focusState,
            length,
            tooltip,
            size,
            rounded,
            label,
            labelGhost,
            arrow,
            color,
            counter,
            attributes,
            leftAdornments,
            rightAdornments,
            keepColor,
            className,
        },
        ref,
    ) => {
        const CustomTag = link ? Link : popover ? "div" : "button";
        const customProps = link
            ? { ...link, ...{ onClick, onMouseEnter, onMouseLeave } }
            : popover
            ? {}
            : { onClick, onMouseEnter, onMouseLeave, type: type || "button", form: form || undefined, tabIndex: 0 };
        const [open, setOpen] = useState(false);

        useEffect(() => {
            openHandler?.(open);
        }, [open]);

        return (
            <Wrapper
                as={CustomTag}
                {...customProps}
                {...attributes}
                id={id}
                disabled={loading || disabled ? true : undefined}
                outline={outline}
                arrow={arrow}
                length={length}
                color={color}
                size={size}
                block={block}
                keepColor={keepColor}
                reflectX={reflectX}
                clean={clean}
                fillIcon={fillIcon}
                largeIcon={largeIcon}
                shadow={shadow}
                label={label}
                rounded={rounded}
                outlineKeepOnHover={outlineKeepOnHover}
                underline={underline}
                active={active}
                focusState={focusState}
                text={text}
                disableHeight={disableHeight}
                tabIndex={tabIndex || customProps.tabIndex || undefined}
                onClick={onClick || undefined}
                className={`group ${active ? "active" : ""} ${className || ""}`.trim()}
                ref={ref}
            >
                {popover ? (
                    <Popover
                        onToggle={setOpen}
                        button={
                            <button>
                                <ButtonBody
                                    tooltip={tooltip}
                                    size={size}
                                    text={text}
                                    disableHeight={disableHeight}
                                    label={label}
                                    labelGhost={labelGhost}
                                    arrow={arrow}
                                    loading={loading}
                                    color={color}
                                    counter={counter}
                                    leftAdornments={leftAdornments}
                                    rightAdornments={rightAdornments}
                                />
                            </button>
                        }
                    >
                        {popover}
                    </Popover>
                ) : (
                    <>
                        <ButtonBody
                            tooltip={tooltip}
                            size={size}
                            text={text}
                            disableHeight={disableHeight}
                            label={label}
                            labelGhost={labelGhost}
                            arrow={arrow}
                            loading={loading}
                            color={color}
                            counter={counter}
                            leftAdornments={leftAdornments}
                            rightAdornments={rightAdornments}
                        />
                    </>
                )}
            </Wrapper>
        );
    },
);

Button.displayName = "Button";
